import { gql, type TypedDocumentNode } from '@apollo/client';
import type { CSSProperties, PropsWithChildren } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes.ts';
import { Vertical } from '@sorare/core/src/atoms/layout/flex/index.tsx';
import { Skeleton } from '@sorare/core/src/atoms/loader/Skeleton/index.tsx';
import {
  BodyL,
  BodyS,
  LabelL,
} from '@sorare/core/src/atoms/typography/index.tsx';
import { TimelineRow } from '@sorare/core/src/components/TimelineRow/index.tsx';
import { RewardConfigImage } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardConfigImage/index.tsx';
import { RewardConfigText } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardConfigText/index.tsx';
import { useSportContext } from '@sorare/core/src/contexts/sport/index.ts';
import { isTypeFn } from '@sorare/core/src/lib/gql/index.ts';

import { InviteTitle } from '#src/components/series/inviteFriends/InviteTitle/index.tsx';

import type { InviteContent_cardSetRoot } from './__generated__/index.graphql.ts';
import footballPlayers from './assets/footballPlayers.webp';
import nbaPlayers from './assets/nbaPlayers.webp';

const Img = styled.img`
  object-fit: contain;
  height: calc(25 * var(--unit));
`;

const getSteps = (gemsAmount: number, essenceAmount: number) => [
  {
    key: '1',
    dotNumber: 1,
    progress: 0.5,
    completed: true,
    title: <FormattedMessage defaultMessage="Share your code" />,
    subtitle: <FormattedMessage defaultMessage="Send your code to a friend" />,
    isLastRow: false,
  },
  {
    key: '2',
    dotNumber: 2,
    progress: 0,
    completed: false,
    title: <FormattedMessage defaultMessage="Have your friends sign up" />,
    subtitle: (
      <FormattedMessage defaultMessage="Make sure they use your code to get the bonus!" />
    ),
    isLastRow: false,
  },
  {
    key: '3',
    dotNumber: 3,
    progress: 0,
    completed: false,
    title: <FormattedMessage defaultMessage="Earn special bonuses" />,
    subtitle: (
      <FormattedMessage
        defaultMessage="{gemsAmount} gems and {essenceAmount} Essence for your friend.<br></br>Cards, gems and Essence for you!"
        values={{
          gemsAmount,
          essenceAmount,
          br: () => <br />,
        }}
      />
    ),
    isLastRow: true,
  },
];

type Props = { cardSetRoot?: InviteContent_cardSetRoot; loading: boolean };
export const InviteContent = ({
  cardSetRoot,
  loading,
  children,
}: PropsWithChildren<Props>) => {
  const lastRewardConfig = cardSetRoot?.inviteTasksAsReferrerRewardConfigs
    .at(-1)
    ?.rewardConfigs.at(0);
  const { sport } = useSportContext();
  const referreeRewardConfigs =
    cardSetRoot?.inviteTaskAsRefereeRewardConfigs.rewardConfigs;
  const gemsAmount =
    referreeRewardConfigs?.find(isTypeFn('InGameCurrencyRewardConfig'))
      ?.amount || 0;
  const essenceAmount =
    referreeRewardConfigs?.find(isTypeFn('CardShardRewardConfig'))?.quantity ||
    0;

  const steps = getSteps(gemsAmount, essenceAmount);

  return (
    <Vertical center gap={4}>
      <Vertical center gap={1.5}>
        <InviteTitle />
        <Skeleton loading={loading}>
          <BodyL className="text-center">
            <FormattedMessage
              defaultMessage="Share your code and <b>earn up to {amount}</b> {unit} and more per invite!"
              values={{
                amount: lastRewardConfig ? (
                  <RewardConfigText anyRewardConfig={lastRewardConfig} />
                ) : null,
                unit: lastRewardConfig ? (
                  <RewardConfigImage
                    anyRewardConfig={lastRewardConfig}
                    size={2.5}
                  />
                ) : null,
                b: chunks => {
                  return (
                    <BodyL as="span" bold>
                      {chunks}
                    </BodyL>
                  );
                },
              }}
            />
          </BodyL>
        </Skeleton>
      </Vertical>
      <Img src={sport === Sport.FOOTBALL ? footballPlayers : nbaPlayers} />
      {children}
      <Vertical
        gap={0}
        style={{ '--progress-color': 'var(--c-white)' } as CSSProperties}
      >
        {steps.map(
          ({ key, dotNumber, progress, title, subtitle, isLastRow }) => {
            return (
              <TimelineRow
                key={key}
                label={
                  <Vertical gap={0.5}>
                    <LabelL bold>{title}</LabelL>
                    <BodyS color="var(--c-nd-600)">{subtitle}</BodyS>
                  </Vertical>
                }
                icon={
                  <LabelL bold>
                    <FormattedNumber value={dotNumber} />
                  </LabelL>
                }
                progress={progress}
                isLastRow={isLastRow}
              />
            );
          }
        )}
      </Vertical>
    </Vertical>
  );
};

InviteContent.fragments = {
  cardSetRoot: gql`
    fragment InviteContent_cardSetRoot on CardSetRoot {
      id
      inviteTasksAsReferrerRewardConfigs(sport: $sport) {
        id
        rewardConfigs {
          id
          ...RewardConfigImage_anyRewardConfigInterface
          ...RewardConfigText_anyRewardConfigInterface
        }
      }
      inviteTaskAsRefereeRewardConfigs(sport: $sport) {
        id
        rewardConfigs {
          id
          ... on InGameCurrencyRewardConfig {
            id
            amount
          }
          ... on CardShardRewardConfig {
            id
            quantity
          }
        }
      }
    }
    ${RewardConfigImage.fragments.anyRewardConfigInterface}
    ${RewardConfigText.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<InviteContent_cardSetRoot>,
};
